/**
 * Media Session — lock-screen and OS-level controls (spec.md §13.4).
 *
 * ## Status: an iOS-only enhancement
 *
 * `navigator.mediaSession` is documented as **unsupported in Android WebView** (MDN BCD
 * `webview_android: false`, crbug 40611412), and Telegram Android renders Mini Apps in a
 * WebView. iOS `WKWebView` has had it since Safari 15. So every function here is written to
 * be a no-op that reports `false` rather than to be relied upon, and the in-app now-playing
 * bar stays the primary control surface on every platform.
 *
 * `ASSUMPTION:` whether the iOS Now Playing card actually appears *inside Telegram's*
 * `WKWebView` depends on the host app's `AVAudioSession` category, which Telegram controls
 * and does not document. **Manual verification step:** on a real iOS device, start a track
 * in the Mini App, lock the screen, and record whether the card appears and whether its
 * transport controls reach these handlers. Until that is recorded, no UI copy may promise
 * lock-screen control.
 *
 * ## Two ways this API bites
 *
 * 1. **`setActionHandler` throws `TypeError` for an action outside the UA's
 *    `MediaSessionAction` enum** — WebIDL enum conversion, not a soft failure. A single
 *    `try/catch` around the whole setup function would therefore abort on the first
 *    unsupported action and leave `play`/`pause` unregistered, i.e. the two that matter
 *    most would be missing on exactly the older clients that need the fallback. Every call
 *    here is in **its own** `try/catch`, and registration is order-independent.
 * 2. **`setPositionState` throws `TypeError`** when `duration` is missing, `NaN` or
 *    negative, when `position > duration`, or when `playbackRate === 0`. All four states
 *    occur naturally: `duration` is `NaN` before `loadedmetadata`, `currentTime` rounds a
 *    hair past `duration` at track end, and a stalled element can report `playbackRate` 0.
 *    {@link syncPositionState} refuses to call through unless the values are provably
 *    valid, which is why it returns a boolean instead of `void`.
 *
 * It is also called on `durationchange` / `seeked` / `play` / `pause` / `ratechange` only —
 * **never** on `timeupdate`, which fires ~4×/second and would turn a lock-screen niceness
 * into a measurable battery cost.
 */

import { audio, type MediaElementLike } from './element';

import { effectiveDuration } from './duration';
import { safePlay } from './unlock';

/* ══ structural types for an API that may not exist ════════════════════════════════ */

/**
 * The eight actions registered, in the exact lowercase single-word spellings the enum
 * uses. `skipad` is deliberately omitted: there are no ads.
 */
export const MEDIA_SESSION_ACTIONS = [
  'play',
  'pause',
  'stop',
  'previoustrack',
  'nexttrack',
  'seekbackward',
  'seekforward',
  'seekto',
] as const;

export type EngineMediaAction = (typeof MEDIA_SESSION_ACTIONS)[number];

/** `details.seekOffset` may be `undefined`; ten seconds is the conventional default. */
export const DEFAULT_SEEK_OFFSET_S = 10;

/** The `MediaSessionActionDetails` fields the handlers read. All optional at runtime. */
export interface MediaSessionActionDetailsLike {
  readonly action?: string;
  readonly fastSeek?: boolean;
  readonly seekOffset?: number;
  readonly seekTime?: number;
}

export interface MediaPositionStateLike {
  readonly duration?: number;
  readonly position?: number;
  readonly playbackRate?: number;
}

/** `'none' | 'paused' | 'playing'` — the three values the enum allows. */
export type MediaPlaybackStateLike = 'none' | 'paused' | 'playing';

/**
 * The runtime shape of `navigator.mediaSession`, modelled loosely on purpose:
 * `setPositionState` arrived after the rest of the interface, so an older iOS has the
 * object without the method.
 */
export interface MediaSessionLike {
  metadata?: unknown;
  playbackState?: string;
  setActionHandler(
    action: string,
    handler: ((details: MediaSessionActionDetailsLike) => void) | null,
  ): void;
  setPositionState?(state?: MediaPositionStateLike): void;
}

/** The `MediaMetadata` constructor, which may be absent even when `mediaSession` is not. */
type MediaMetadataConstructor = new (init: {
  title?: string;
  artist?: string;
  album?: string;
  artwork?: readonly { src: string; sizes?: string; type?: string }[];
}) => unknown;

export interface NowPlayingArtwork {
  readonly src: string;
  readonly sizes?: string;
  readonly type?: string;
}

export interface NowPlayingMetadata {
  readonly title: string;
  readonly artist: string;
  readonly album?: string;
  readonly artwork?: readonly NowPlayingArtwork[];
}

/* ══ resolution ════════════════════════════════════════════════════════════════════ */

/**
 * `navigator.mediaSession` when it exists, else `null`.
 *
 * The double assertion is deliberate: `lib.dom.d.ts` types the property as a
 * **non-optional** `MediaSession`, which is a lie on every Android client, and
 * {@link MediaSessionLike} is intentionally looser than the DOM interface (optional
 * `setPositionState`, `string` action names). Going through `unknown` states that
 * mismatch instead of hiding it behind a structural coincidence.
 */
export function resolveMediaSession(): MediaSessionLike | null {
  if (typeof navigator === 'undefined') return null;
  try {
    if (!('mediaSession' in navigator)) return null;
    const session = (navigator as unknown as { mediaSession?: unknown }).mediaSession;
    if (typeof session !== 'object' || session === null) return null;
    const candidate = session as MediaSessionLike;
    return typeof candidate.setActionHandler === 'function' ? candidate : null;
  } catch {
    // Reading the property can throw in a hardened webview.
    return null;
  }
}

function resolveMetadataConstructor(): MediaMetadataConstructor | null {
  const ctor = (globalThis as { MediaMetadata?: unknown }).MediaMetadata;
  return typeof ctor === 'function' ? (ctor as MediaMetadataConstructor) : null;
}

/* ══ action handlers ═══════════════════════════════════════════════════════════════ */

/**
 * Overrides for the default element-driven behaviour.
 *
 * `previoustrack` and `nexttrack` have **no** default: the engine does not own the queue.
 * They are registered only when supplied, because registering a no-op would put a live
 * next-track button on the lock screen that does nothing.
 */
export interface MediaSessionHandlers {
  readonly play?: () => void;
  readonly pause?: () => void;
  readonly stop?: () => void;
  readonly previoustrack?: () => void;
  readonly nexttrack?: () => void;
  /** Receives the resolved offset in seconds (`details.seekOffset ?? 10`). */
  readonly seekbackward?: (offsetS: number) => void;
  /** Receives the resolved offset in seconds (`details.seekOffset ?? 10`). */
  readonly seekforward?: (offsetS: number) => void;
  /** Receives the target time in seconds and whether the UA asked for a fast seek. */
  readonly seekto?: (timeS: number, fast: boolean) => void;
}

export interface SetupOptions {
  readonly el?: MediaElementLike;
  /** Injectable session, for tests. `null` forces the unsupported path. */
  readonly session?: MediaSessionLike | null;
  /** Server-side duration in seconds, used to clamp `seekforward`/`seekto`. */
  readonly durationS?: number;
}

export interface MediaSessionSetup {
  /** `false` when the platform has no Media Session at all (every Android client). */
  readonly supported: boolean;
  readonly registered: readonly EngineMediaAction[];
  /** Actions whose `setActionHandler` threw — an older UA's enum rejecting the name. */
  readonly failed: readonly EngineMediaAction[];
  /** Clears every handler this setup registered. Safe to call more than once. */
  teardown(): void;
}

function clampSeek(target: number, el: MediaElementLike, durationS: number): number {
  const duration = effectiveDuration(durationS, el);
  const lower = Math.max(0, target);
  return duration > 0 ? Math.min(lower, duration) : lower;
}

function currentTimeOf(el: MediaElementLike): number {
  const t = el.currentTime;
  return Number.isFinite(t) && t >= 0 ? t : 0;
}

/**
 * Builds the handler for one action, or `null` when the action has no meaning without a
 * caller-supplied callback.
 */
function buildHandler(
  action: EngineMediaAction,
  handlers: MediaSessionHandlers,
  el: MediaElementLike,
  durationS: number,
): ((details: MediaSessionActionDetailsLike) => void) | null {
  switch (action) {
    case 'play':
      return () => {
        const override = handlers.play;
        if (override !== undefined) {
          override();
          return;
        }
        void safePlay(undefined, el);
      };

    case 'pause':
      return () => {
        const override = handlers.pause;
        if (override !== undefined) {
          override();
          return;
        }
        el.pause();
      };

    case 'stop':
      return () => {
        const override = handlers.stop;
        if (override !== undefined) {
          override();
          return;
        }
        el.pause();
        el.currentTime = 0;
      };

    case 'previoustrack': {
      const override = handlers.previoustrack;
      return override === undefined
        ? null
        : () => {
            override();
          };
    }

    case 'nexttrack': {
      const override = handlers.nexttrack;
      return override === undefined
        ? null
        : () => {
            override();
          };
    }

    case 'seekbackward':
      return (details) => {
        const offset = details.seekOffset ?? DEFAULT_SEEK_OFFSET_S;
        const resolved = Number.isFinite(offset) && offset > 0 ? offset : DEFAULT_SEEK_OFFSET_S;
        const override = handlers.seekbackward;
        if (override !== undefined) {
          override(resolved);
          return;
        }
        el.currentTime = clampSeek(currentTimeOf(el) - resolved, el, durationS);
      };

    case 'seekforward':
      return (details) => {
        const offset = details.seekOffset ?? DEFAULT_SEEK_OFFSET_S;
        const resolved = Number.isFinite(offset) && offset > 0 ? offset : DEFAULT_SEEK_OFFSET_S;
        const override = handlers.seekforward;
        if (override !== undefined) {
          override(resolved);
          return;
        }
        el.currentTime = clampSeek(currentTimeOf(el) + resolved, el, durationS);
      };

    case 'seekto':
      return (details) => {
        const requested = details.seekTime;
        if (requested === undefined || !Number.isFinite(requested)) return;
        const target = clampSeek(requested, el, durationS);
        const override = handlers.seekto;
        if (override !== undefined) {
          override(target, details.fastSeek === true);
          return;
        }
        // `fastSeek` is in the IDL but NOT implemented in Chromium, so the typeof guard
        // is doing real work — without it this is a `TypeError` on Android.
        if (details.fastSeek === true && typeof el.fastSeek === 'function') {
          el.fastSeek(target);
          return;
        }
        el.currentTime = target;
      };

    default:
      return null;
  }
}

/**
 * Registers the action handlers.
 *
 * Returns a report rather than throwing: on Android there is nothing to register, and on an
 * older iOS one or two action names will be rejected by the enum. Both are normal.
 */
export function setupMediaSession(
  handlers: MediaSessionHandlers = {},
  options: SetupOptions = {},
): MediaSessionSetup {
  const el = options.el ?? audio;
  const durationS = options.durationS ?? 0;
  const session = options.session === undefined ? resolveMediaSession() : options.session;

  const registered: EngineMediaAction[] = [];
  const failed: EngineMediaAction[] = [];

  if (session === null) {
    return { supported: false, registered, failed, teardown: () => {} };
  }

  for (const action of MEDIA_SESSION_ACTIONS) {
    const handler = buildHandler(action, handlers, el, durationS);
    if (handler === null) continue;
    // One try/catch PER CALL: a TypeError from an unsupported action name must not skip
    // the actions after it. See the module header.
    try {
      session.setActionHandler(action, handler);
      registered.push(action);
    } catch {
      failed.push(action);
    }
  }

  let torn = false;
  const teardown = (): void => {
    if (torn) return;
    torn = true;
    for (const action of registered) {
      try {
        session.setActionHandler(action, null);
      } catch {
        // Nothing to do: the handler is going away with the page anyway.
      }
    }
  };

  return { supported: true, registered, failed, teardown };
}

/* ══ metadata ══════════════════════════════════════════════════════════════════════ */

export interface MetadataOptions {
  readonly session?: MediaSessionLike | null;
}

/**
 * Publishes the now-playing metadata. `true` when it was accepted.
 *
 * Requires both `navigator.mediaSession` **and** the `MediaMetadata` constructor: an
 * engine can have the former without the latter, and assigning a plain object to
 * `metadata` is silently ignored rather than throwing, which would make a failure
 * invisible.
 */
export function setNowPlaying(meta: NowPlayingMetadata, options: MetadataOptions = {}): boolean {
  const session = options.session === undefined ? resolveMediaSession() : options.session;
  if (session === null) return false;
  const Ctor = resolveMetadataConstructor();
  if (Ctor === null) return false;
  try {
    session.metadata = new Ctor({
      title: meta.title,
      artist: meta.artist,
      ...(meta.album === undefined ? {} : { album: meta.album }),
      ...(meta.artwork === undefined ? {} : { artwork: meta.artwork }),
    });
    return true;
  } catch {
    return false;
  }
}

/** Mirrors the real `paused` state onto the OS. `true` when it was accepted. */
export function setPlaybackState(
  state: MediaPlaybackStateLike,
  options: MetadataOptions = {},
): boolean {
  const session = options.session === undefined ? resolveMediaSession() : options.session;
  if (session === null) return false;
  try {
    session.playbackState = state;
    return true;
  } catch {
    return false;
  }
}

/* ══ position state ════════════════════════════════════════════════════════════════ */

export interface PositionStateOptions {
  readonly el?: MediaElementLike;
  readonly session?: MediaSessionLike | null;
}

/**
 * Pushes `{ duration, position, playbackRate }` to the OS, **only** when all three are
 * provably valid.
 *
 * The four states that would make `setPositionState` throw are each excluded before the
 * call, not caught after it:
 *
 * - `duration` missing / `NaN` / `Infinity` / `<= 0` → return `false` without calling. The
 *   window between `play()` and `loadedmetadata` is exactly this state, and it is the one
 *   a naive implementation hits on every single track.
 * - `position > duration` → clamped. `currentTime` really does round past `duration` at
 *   the end of a track, by a few microseconds.
 * - `playbackRate === 0` (or `NaN`) → coerced to `1` via `|| 1`.
 *
 * @param serverDurationS the catalogue duration, used until the element knows its own
 * @returns `true` when the platform accepted a position update
 */
export function syncPositionState(
  serverDurationS = 0,
  options: PositionStateOptions = {},
): boolean {
  const el = options.el ?? audio;
  const session = options.session === undefined ? resolveMediaSession() : options.session;
  if (session === null || typeof session.setPositionState !== 'function') return false;

  const duration = effectiveDuration(serverDurationS, el);
  if (!Number.isFinite(duration) || duration <= 0) return false;

  const rawPosition = el.currentTime;
  const safePosition = Number.isFinite(rawPosition) && rawPosition > 0 ? rawPosition : 0;
  const position = Math.min(safePosition, duration);

  const rawRate = el.playbackRate;
  const playbackRate = (Number.isFinite(rawRate) ? rawRate : 0) || 1;

  try {
    session.setPositionState({ duration, position, playbackRate });
    return true;
  } catch {
    return false;
  }
}

/**
 * Clears the position state, which is what `setPositionState({})` is specified to do.
 *
 * Called on **track change**, before the new source has any metadata: without it the OS
 * keeps showing the previous track's duration and a playhead that appears to jump
 * backwards.
 */
export function clearPositionState(options: MetadataOptions = {}): boolean {
  const session = options.session === undefined ? resolveMediaSession() : options.session;
  if (session === null || typeof session.setPositionState !== 'function') return false;
  try {
    session.setPositionState({});
    return true;
  } catch {
    return false;
  }
}
